import { useCallback, useEffect, useRef, useState } from 'react';

import {
    IoPlayBackSharp,
    IoPlayForwardSharp,
    IoPlaySkipBackSharp,
    IoPlaySkipForwardSharp,
    IoPlaySharp,
    IoPauseSharp,
} from 'react-icons/io5';

const formatTime = (seconds) => {
    if (seconds && !isNaN(seconds)) {
      const minutes = Math.floor(seconds / 60);
      const formatMinutes =
        minutes < 10 ? `0${minutes}` : `${minutes}`;
      const newSeconds = Math.floor(seconds % 60);
      const formatSeconds =
      newSeconds < 10 ? `0${newSeconds}` : `${newSeconds}`;
      return `${formatMinutes}:${formatSeconds}`;
    }
    return '00:00';
};

const MusicPlayer = ({ playlist = [] }) => {
    const [trackIndex, setTrackIndex] = useState(0);
    const [currentTrack, setCurrentTrack] = useState(playlist[trackIndex]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [timeProgress, setTimeProgress] = useState(0);
    const [duration, setDuration] = useState(0);


    const musicPlayerRef = useRef();
    const progressBarRef = useRef();
    const playAnimationRef = useRef();

    const togglePlayPause = () => {
        setIsPlaying((prev) => !prev);
    };

    const handleProgressChange = () => {
        musicPlayerRef.current.currentTime = progressBarRef.current.value;
    };

    const onLoadedMetadata = () => {
        const seconds = musicPlayerRef.current.duration;
        setDuration(seconds);
        progressBarRef.current.max = seconds;
    }

    const animateOneFrameMusicProgress = useCallback(() => {
        const currentTime = musicPlayerRef.current?.currentTime;

        // Ensure the progress bar progresses with the current audio time
        progressBarRef.current.value = currentTime;

        // Dynamically update the audio progress: style the length of the played section
        // via the CSS variable --range-progress
        progressBarRef.current?.style?.setProperty(
            '--range-progress',
            `${(currentTime / duration) * 100}%`
        );

        // Update the timeProgress state variable with the current audio time
        setTimeProgress(currentTime);
        
        // Recursively call to repeat the animation as the audio progresses
        playAnimationRef.current = requestAnimationFrame(animateOneFrameMusicProgress);
    }, [musicPlayerRef, duration, progressBarRef, setTimeProgress]);

    useEffect(() => {
        if (isPlaying) {
          musicPlayerRef.current.play();
        } else {
          musicPlayerRef.current.pause();
        }

        playAnimationRef.current = requestAnimationFrame(animateOneFrameMusicProgress);
    }, [isPlaying, musicPlayerRef, animateOneFrameMusicProgress]);

    const skipForward = useCallback(() => {
        musicPlayerRef.current.currentTime += 5;
    }, [musicPlayerRef]);

    const skipBackward = useCallback(() => {
        musicPlayerRef.current.currentTime -= 5;
    }, [musicPlayerRef]);

    const changeToPreviousTrack = useCallback(() => {
        if (trackIndex === 0) {
            let lastTrackIndex = playlist.length - 1;
            setTrackIndex(lastTrackIndex);
            setCurrentTrack(playlist[lastTrackIndex]);
        } else {
            setTrackIndex((prev) => prev - 1);
            setCurrentTrack(playlist[trackIndex - 1]);
          }
    }, [trackIndex, playlist, setTrackIndex, setCurrentTrack]);

    const changeToNextTrack = useCallback(() => {
        if (trackIndex >= playlist.length - 1) {
            setTrackIndex(0);
            setCurrentTrack(playlist[0]);
        } else {
            setTrackIndex((prev) => prev + 1);
            setCurrentTrack(playlist[trackIndex + 1]);
        }
    }, [trackIndex, playlist, setTrackIndex, setCurrentTrack]);

    return (
        <div className="music-player-content row justify-content-center align-items-center">
            <audio 
                src={currentTrack?.url} 
                ref={musicPlayerRef}
                onLoadedMetadata={onLoadedMetadata}
                onEnded={changeToNextTrack}
            />

            <img src={currentTrack.cover} className="music-cover"/>

            <div className="music-info-and-controls">
                <div>
                    <p className="title font-josefin" id="music-title">{currentTrack.title}</p>
                    <p className="font-josefin" id="artist-name">{currentTrack.artist}</p>
                </div>

                <div className="music-progress">
                    <span id="total-music-time" className="font-josefin-light">-{formatTime(duration - timeProgress)}</span>
                    <input 
                        type="range" 
                        ref={progressBarRef}
                        defaultValue="0"
                        onChange={handleProgressChange}
                    />
                </div>

                <div className="controls-wrapper">
                    <div className="controls">
                        <button>
                            <IoPlaySkipBackSharp color="#3490ca" onClick={changeToPreviousTrack} />
                        </button>
                        <button>
                            <IoPlayBackSharp color="#3490ca" onClick={skipBackward} />
                        </button>

                        <button onClick={togglePlayPause}>
                            {isPlaying ? <IoPauseSharp color="#3490ca" /> : <IoPlaySharp color="#3490ca" />}
                        </button>
                        <button>
                            <IoPlayForwardSharp color="#3490ca" onClick={skipForward} />
                        </button>
                        <button>
                            <IoPlaySkipForwardSharp color="#3490ca" onClick={changeToNextTrack} />
                        </button>
                    </div>
                </div>
            </div>   
        </div>
    );
  };
  export default MusicPlayer;